import React, { useState, useEffect } from 'react';
import { FaUserFriends, FaTrophy, FaCalendarAlt, FaGolfBall } from 'react-icons/fa';
import { fetchPlayers, fetchScores, fetchCourses } from '../utils/api';
import './Players.css';

const Players = () => {
  const [players, setPlayers] = useState([]);
  const [scores, setScores] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [playersData, scoresData, coursesData] = await Promise.all([
          fetchPlayers(),
          fetchScores(),
          fetchCourses()
        ]);
        
        if (playersData.error) {
          setError(playersData.message);
          return;
        }
        
        if (scoresData.error) {
          setError(scoresData.message);
          return;
        }
        
        if (coursesData.error) {
          setError(coursesData.message);
          return;
        }
        
        setPlayers(playersData);
        setScores(scoresData);
        setCourses(coursesData);
      } catch (err) {
        setError('Failed to load player data. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="container">
        <div className="page-title">
          <FaUserFriends />
          <h1>Spelare</h1>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="page-title">
          <FaUserFriends />
          <h1>Spelare</h1>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  // Create a courses lookup object
  const coursesLookup = {};
  courses.forEach(course => {
    coursesLookup[course.name] = {
      id: course.id,
      holes: course.holes,
      par: course.par
    };
  });

  // Process player stats
  const playerStats = players.map(player => {
    // Get player's scores
    const playerScores = scores.filter(score => score.player_id === player.id);
    
    // Best score per course
    const bestScoresByCourse = {};
    playerScores.forEach(score => {
      const courseName = score.course_name;
      if (!bestScoresByCourse[courseName] || score.score < bestScoresByCourse[courseName].score) {
        bestScoresByCourse[courseName] = {
          score: score.score,
          date: score.date
        };
      }
    });
    
    // Calculate overall average score
    const averageScore = playerScores.length > 0 
      ? (playerScores.reduce((sum, score) => sum + score.score, 0) / playerScores.length).toFixed(1)
      : '-';
    
    // Calculate average scores per hole count (9, 12, 18)
    const scoresByHoleCount = {
      9: [],
      12: [],
      18: []
    };
    
    playerScores.forEach(score => {
      const courseInfo = coursesLookup[score.course_name];
      if (courseInfo && [9, 12, 18].includes(courseInfo.holes)) {
        scoresByHoleCount[courseInfo.holes].push(score.score);
      }
    });
    
    const averageScoreByHoles = {};
    Object.entries(scoresByHoleCount).forEach(([holes, scores]) => {
      if (scores.length > 0) {
        averageScoreByHoles[holes] = (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1);
      } else {
        averageScoreByHoles[holes] = '-';
      }
    });
    
    // Get latest round
    const latestRound = playerScores.length > 0
      ? playerScores.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
      : null;

    return {
      ...player,
      roundsPlayed: playerScores.length,
      bestScoresByCourse,
      averageScore,
      averageScoreByHoles,
      latestRound
    };
  });

  // Sort by number of rounds played (descending)
  playerStats.sort((a, b) => b.roundsPlayed - a.roundsPlayed);

  // Format date 
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format score relative to par
  const formatScoreRelativeToPar = (score, par) => {
    if (!par) return score;
    
    const diff = score - par;
    if (diff === 0) return `${score} (Par)`;
    return diff > 0 ? `${score} (+${diff})` : `${score} (${diff})`;
  };

  return (
    <div className="container">
      <div className="page-title">
        <FaUserFriends />
        <h1>Spelare</h1>
      </div>

      <div className="players-grid">
        {playerStats.map(player => (
          <div key={player.id} className="player-card card">
            <div className="player-header">
              <h2>{player.name}</h2>
              <div className="player-rounds">
                <FaCalendarAlt />
                <span>{player.roundsPlayed} rundor</span>
              </div>
            </div>
            
            <div className="player-stats">
              <div className="stat-item">
                <div className="stat-label">Snitt</div>
                <div className="stat-value">{player.averageScore}</div>
              </div>
              
              <div className="stat-item">
                <div className="stat-label">Senaste runda</div>
                <div className="stat-value">
                  {player.latestRound ? (
                    <>
                      <span>{player.latestRound.score}</span>
                      <small>{player.latestRound.course_name}, {formatDate(player.latestRound.date)}</small>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
            
            <div className="player-average-by-holes">
              <h3>
                <FaGolfBall /> Snitt per bantyp
              </h3>
              <div className="average-holes-grid">
                <div className="average-holes-item">
                  <div className="holes-label">9 hål</div>
                  <div className="holes-value">{player.averageScoreByHoles['9']}</div>
                </div>
                <div className="average-holes-item">
                  <div className="holes-label">12 hål</div>
                  <div className="holes-value">{player.averageScoreByHoles['12']}</div>
                </div>
                <div className="average-holes-item">
                  <div className="holes-label">18 hål</div>
                  <div className="holes-value">{player.averageScoreByHoles['18']}</div>
                </div>
              </div>
            </div>
            
            <div className="player-best-scores">
              <h3>
                <FaTrophy /> Bästa resultat per bana
              </h3>
              {Object.keys(player.bestScoresByCourse).length > 0 ? (
                <ul className="best-scores-list">
                  {Object.entries(player.bestScoresByCourse).map(([course, data]) => {
                    const courseInfo = coursesLookup[course];
                    const par = courseInfo ? courseInfo.par : null;
                    
                    return (
                      <li key={course}>
                        <span className="course-name">{course}:</span>
                        <strong>
                          {par ? formatScoreRelativeToPar(data.score, par) : data.score}
                        </strong>
                        <small>({formatDate(data.date)})</small>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="no-scores">Inga rundor spelade än.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Players; 